import { Component, OnInit, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CatalogService, RetailMediaCampaignRecord } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-marketplace-retail-media',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, TableModule, ButtonModule, TagModule],
  template: `
    <ain-page
      [title]="t('catalog.marketplace.retailMedia.title')"
      [description]="t('catalog.marketplace.retailMedia.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.retailMedia.help.title'"
        [descriptionKey]="'catalog.marketplace.retailMedia.help.description'"
        [bulletKeys]="[
          'catalog.marketplace.retailMedia.help.bullets.wallet',
          'catalog.marketplace.retailMedia.help.bullets.approval'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <p-table [value]="campaigns()" [tableStyle]="{ 'min-width': '52rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.marketplace.retailMedia.columns.code') }}</th>
              <th>{{ t('catalog.marketplace.retailMedia.columns.name') }}</th>
              <th>{{ t('catalog.marketplace.retailMedia.columns.status') }}</th>
              <th>{{ t('catalog.marketplace.retailMedia.columns.spent') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.campaignCode }}</td>
              <td>{{ row.name }}</td>
              <td><p-tag [value]="row.status" /></td>
              <td>{{ row.spentAmount }} {{ row.currencyCode }}</td>
              <td>
                @if (row.status === 'PendingReview') {
                  <button pButton type="button" size="small" [label]="t('catalog.marketplace.retailMedia.approve')" (click)="approve(row.id)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `
})
export class MarketplaceRetailMediaComponent implements OnInit {
  private readonly api = inject(CatalogService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly campaigns = signal<RetailMediaCampaignRecord[]>([]);

  ngOnInit(): void {
    this.reload();
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  reload(): void {
    this.loading.set(true);
    this.api.listRetailMediaCampaigns().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: rows => this.campaigns.set(rows ?? []),
      error: () => this.campaigns.set([])
    });
  }

  approve(campaignId: string): void {
    this.api.approveRetailMediaCampaign(campaignId).subscribe(() => this.reload());
  }
}
